// ---------------------------------------------------------------------------
// Sous-titres : rendu maison et reglages.
//
// Le rendu natif est ecarte. `::cue` ne laisse regler qu'une poignee de choses
// — couleur, fond, police — et rien de ce qui compte ici : ni contour, ni
// position, ni largeur, ni ombre. Les pistes sont donc passees en `hidden`,
// ce qui les fait decoder sans les afficher, et le texte est pose nous-memes
// dans un calque. A partir de la, tout est reglable.
//
// Le choix de la LANGUE (quelle piste, parmi celles du fichier) ne vit pas
// ici : c'est l'onglet « Piste » de `st-panneau.js` qui le porte, pleinement
// fonctionnel — ce module ne s'occupe que de l'apparence et du rendu du
// texte, une fois la piste choisie.
// ---------------------------------------------------------------------------

import { dansLaReserve, chargerReserve } from './polices-reserve.js';

const CLE = '14d:soustitres';

export const DEFAUTS = {
  police: 'system-ui',
  taille: 100, // pourcentage
  graisse: 600,
  italique: false,
  majuscules: false,
  couleur: '#ffffff',
  opacite: 100,
  contour: 2.4, // px
  couleurContour: '#000000',
  ombre: 'douce', // aucune | douce | marquee | relief
  fond: '#000000',
  opaciteFond: 0, // 0 = pas de cartouche
  rayonFond: 4,
  position: 'bas', // bas | milieu | haut
  decalage: 8, // % de la hauteur, depuis le bord choisi
  largeur: 80, // % de la largeur du cadre
  interligne: 1.35,
  alignement: 'center',
  decalageTemps: 0, // secondes, pour recaler une piste en avance ou en retard
};

// ---------------------------------------------------------------------------
// Catalogue de polices.
//
// Trois provenances, cumulables : celles que la page charge deja, celles que
// tout systeme fournit, et — sur demande explicite — celles installees sur la
// machine, via l'API d'acces aux polices locales. Cette derniere demande une
// autorisation : elle n'est donc jamais sollicitee toute seule.
// ---------------------------------------------------------------------------
export const FAMILLES = [
  {
    nom: 'Sans serif',
    polices: [
      'system-ui', 'Segoe UI', 'Roboto', 'Helvetica Neue', 'Arial', 'Verdana',
      'Tahoma', 'Trebuchet MS', 'Calibri', 'Candara', 'Corbel', 'Franklin Gothic Medium',
      'Century Gothic', 'Lucida Sans Unicode', 'Bahnschrift', 'Gill Sans MT',
      'Zen Kaku Gothic New', 'Archivo Narrow',
    ],
  },
  {
    nom: 'Serif',
    polices: [
      'Georgia', 'Times New Roman', 'Garamond', 'Book Antiqua', 'Palatino Linotype',
      'Cambria', 'Constantia', 'Bookman Old Style', 'Rockwell', 'Baskerville Old Face',
      'Shippori Mincho',
    ],
  },
  {
    nom: 'Affiche',
    polices: ['Impact', 'Haettenschweiler', 'Arial Black', 'Bebas Neue', 'Agency FB', 'Copperplate Gothic Bold'],
  },
  {
    nom: 'Chasse fixe',
    polices: ['Consolas', 'Courier New', 'Cascadia Mono', 'Lucida Console', 'Menlo', 'monospace'],
  },
  {
    nom: 'Manuscrite',
    polices: ['Segoe Script', 'Brush Script MT', 'Comic Sans MS', 'Ink Free', 'Lucida Handwriting'],
  },
];

/**
 * Ajoute les polices installees sur la machine. Le navigateur demande son
 * accord a l'utilisateur ; un refus, ou un navigateur sans cette API, laisse le
 * catalogue tel quel — ce n'est pas une panne.
 */
export async function policesLocales() {
  if (typeof window === 'undefined' || !window.queryLocalFonts) {
    return { ok: false, raison: "ce navigateur n'ouvre pas l'accès aux polices du système" };
  }
  try {
    const trouvees = await window.queryLocalFonts();
    const familles = [...new Set(trouvees.map((f) => f.family))].sort((a, b) => a.localeCompare(b, 'fr'));
    return { ok: true, familles };
  } catch (err) {
    return { ok: false, raison: err.name === 'NotAllowedError' ? 'accès refusé' : err.message };
  }
}

// ---------------------------------------------------------------------------
// Persistance
// ---------------------------------------------------------------------------
export function lireReglages() {
  try {
    const brut = localStorage.getItem(CLE);
    return brut ? { ...DEFAUTS, ...JSON.parse(brut) } : { ...DEFAUTS };
  } catch {
    return { ...DEFAUTS };
  }
}

export function ecrireReglages(r) {
  try {
    localStorage.setItem(CLE, JSON.stringify(r));
  } catch { /* stockage refuse : les reglages valent pour la session */ }
}

// ---------------------------------------------------------------------------
// Traduction des reglages en style
// ---------------------------------------------------------------------------
const OMBRES = {
  aucune: 'none',
  douce: '0 2px 4px rgba(0,0,0,.75)',
  marquee: '0 3px 10px rgba(0,0,0,.95), 0 1px 2px rgba(0,0,0,.9)',
  relief: '2px 2px 0 rgba(0,0,0,.9)',
};

const hexRgba = (hex, pourcent) => {
  const n = String(hex).replace('#', '');
  const v = n.length === 3 ? n.split('').map((c) => c + c).join('') : n;
  const r = parseInt(v.slice(0, 2), 16) || 0;
  const g = parseInt(v.slice(2, 4), 16) || 0;
  const b = parseInt(v.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(100, pourcent)) / 100})`;
};

/**
 * Applique les reglages au calque. Le contour passe par `paint-order`, qui
 * dessine le trait SOUS le remplissage : sans lui, un contour epais ronge
 * l'interieur des lettres et les rend illisibles.
 */
export function appliquerStyle(calque, r) {
  if (!calque) return;
  const boite = calque.querySelector('.st-boite');
  if (!boite) return;

  calque.style.setProperty('--st-largeur', `${r.largeur}%`);
  // En colonne, c'est `justifyContent` qui place sur la hauteur.
  calque.style.justifyContent = r.position === 'haut' ? 'flex-start'
    : r.position === 'milieu' ? 'center' : 'flex-end';
  calque.style.alignItems = r.alignement === 'left' ? 'flex-start'
    : r.alignement === 'right' ? 'flex-end' : 'center';
  // ⚠️ UN PLANCHER, SINON LA DEUXIEME LIGNE SORT DE L'ECRAN.
  //
  // Toji, capture a l'appui : la premiere ligne se lit, la seconde est coupee
  // par le bord bas. Le calque colle son contenu en bas (`flex-end`) et le
  // decalage seul l'en ecarte : a zero — ou avec une valeur basse posee un jour
  // sur une replique d'une seule ligne — une replique de deux lignes deborde,
  // et c'est toujours la SECONDE qu'on perd, celle qui finit la phrase.
  //
  // Le reglage garde la main pour eloigner davantage ; il ne peut plus
  // rapprocher au point de faire sortir le texte.
  // Borné des deux côtés (21/10) : au-delà de 40 % une réplique de trois
  // lignes sortait par l'autre bord. Les bornes sont celles du curseur.
  const ecart = `${Math.min(40, Math.max(6, Number(r.decalage) || 0))}%`;
  // Par variables, pas en ligne : la feuille relève le texte au-dessus de la
  // barre quand les commandes sont affichées (`.lecteur:not(.inactif)`).
  calque.dataset.position = r.position;
  calque.style.setProperty('--st-ecart-haut', r.position === 'haut' ? ecart : '0px');
  calque.style.setProperty('--st-ecart-bas', r.position === 'bas' ? ecart : '0px');

  const s = boite.style;
  if (dansLaReserve(r.police)) chargerReserve();
  s.fontFamily = `"${r.police}", system-ui, sans-serif`;
  s.fontSize = `${r.taille}%`;
  s.fontWeight = String(r.graisse);
  s.fontStyle = r.italique ? 'italic' : 'normal';
  s.textTransform = r.majuscules ? 'uppercase' : 'none';
  s.color = hexRgba(r.couleur, r.opacite);
  s.lineHeight = String(r.interligne);
  s.textAlign = r.alignement;
  s.background = r.opaciteFond > 0 ? hexRgba(r.fond, r.opaciteFond) : 'transparent';
  s.borderRadius = `${r.rayonFond}px`;
  s.padding = r.opaciteFond > 0 ? '.25em .6em' : '0';
  s.textShadow = OMBRES[r.ombre] ?? OMBRES.douce;
  s.webkitTextStrokeWidth = `${r.contour}px`;
  s.webkitTextStrokeColor = r.couleurContour;
  s.paintOrder = 'stroke fill';
}

// ---------------------------------------------------------------------------
// Rendu du texte
// ---------------------------------------------------------------------------

/**
 * Suit la piste active et pose son texte dans le calque.
 * `positionCourante` rend la seconde reellement lue — elle tient compte du
 * decalage du remux, que la video ignore.
 */
export function brancherRendu({ video, calque, reglages, positionCourante }) {
  // ⚠️ GARDE CONTRE UN SECOND BRANCHEMENT SUR LA MÊME BALISE.
  //
  // `seance.js` pose le même genre de marqueur sur `<video>` (`dataset.seance`)
  // pour la même raison : rien n'empêchait techniquement un second appel
  // accidentel de reposer un jeu complet d'écouteurs (`timeupdate`, `seeked`,
  // `loadedmetadata`, une `cuechange` par piste, `addtrack`) par-dessus les
  // précédents — chaque réplique se serait alors peinte deux fois, un défaut
  // silencieux tant que personne ne le remarque à l'œil. `creerLecteur`, dans
  // `lecteur.js`, ne monte aujourd'hui qu'une seule fois par page (« monté une
  // seule fois, au premier usage »), ce qui rend le cas théorique pour
  // l'instant — mais la garde ne coûte rien, et rend explicite une hypothèse
  // que le reste du fichier fait déjà sans le dire : une seule instance de
  // rendu par balise.
  if (video.dataset.stRendu === '1') {
    console.warn('[sous-titres] brancherRendu appelé deux fois sur la même balise — ignoré');
    return { peindre: () => {}, apercu: () => {} };
  }
  video.dataset.stRendu = '1';

  const boite = calque.querySelector('.st-boite');
  let exemple = null; // texte d'apercu, prioritaire sur la piste

  // ⚠️ HYPOTHESE SILENCIEUSE : UNE SEULE PISTE ACTIVE A LA FOIS.
  //
  // `.find()` s'arrête à la première piste en mode 'hidden' ou 'showing', dans
  // l'ORDRE du fichier — jamais la plus récemment choisie. Cette fonction ne
  // vérifie jamais qu'il n'y en a pas une deuxième : si `activerPiste`
  // (main.js) et le panneau de sous-titres (`st-panneau.js`) laissaient un
  // jour deux pistes actives en même temps — un clic rapproché sur deux
  // pistes différentes, par exemple — c'est celle-ci, déterministe mais pas
  // forcément celle qu'on vient de choisir, qui gagnerait le rendu.
  function pisteActive() {
    return [...video.textTracks].find((p) => p.mode === 'hidden' || p.mode === 'showing') ?? null;
  }

  // ⚠️ LA RÉPLIQUE EN COURS GAGNE SUR LA LIGNE TÉMOIN (Toji, 21/10). Panneau
  // ouvert, le texte d'exemple REMPLAÇAIT la réplique affichée : on réglait la
  // police sur une phrase qui n'était pas celle qu'on regardait. Le témoin ne
  // sert plus que quand rien n'est à l'écran, et cède dès qu'une réplique
  // arrive (`cuechange` repeint).
  function temoin() {
    if (exemple === null) return false;
    boite.innerHTML = exemple;
    calque.hidden = false;
    return true;
  }

  function peindre() {
    const piste = pisteActive();
    if (!piste || !piste.cues) {
      if (!temoin()) calque.hidden = true;
      return;
    }

    // Le decalage de synchronisation se lit ici : on cherche les repliques a
    // l'instant voulu plutot que de deplacer la video.
    const t = positionCourante() - (reglages().decalageTemps || 0);
    const textes = [];
    for (const c of piste.cues) {
      if (c.startTime <= t && t <= c.endTime) textes.push(c.text);
    }

    if (!textes.length) {
      if (temoin()) return;
      calque.hidden = true;
      boite.textContent = '';
      return;
    }
    // Le texte d'une replique peut porter un balisage simple (italique, gras) :
    // il est conserve, mais rien d'autre ne passe.
    boite.innerHTML = textes
      .join('\n')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/&lt;(\/?(?:i|b|u))&gt;/g, '<$1>')
      .replace(/\n/g, '<br>');
    calque.hidden = false;
  }

  video.addEventListener('timeupdate', peindre);
  // Les répliques arrivent parfois après le branchement : sans cet écouteur, la
  // première resterait invisible jusqu'au changement suivant.
  for (const p of video.textTracks) p.addEventListener('cuechange', peindre);
  video.textTracks.addEventListener?.('addtrack', (e) => {
    e.track?.addEventListener('cuechange', peindre);
    peindre();
  });
  video.addEventListener('seeked', peindre);
  video.addEventListener('loadedmetadata', peindre);

  return {
    peindre,
    /** Fige un texte d'apercu, ou rend la main aux repliques avec `null`. */
    apercu(texte) {
      exemple = texte;
      peindre();
    },
  };
}
